import {useEffect, useState} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import useGrantApplicationAction from "./useGrantApplicationAction";
import {useSelector} from "react-redux";
import {useHistory} from "react-router-dom";
import {getRouteUrl} from "../../helpers/routing/getRouteUrl";
import {ROUTE_APPLICATIONS, ROUTE_NEW_APPLICATION, ROUTE_PAGE_HOME} from "../../routers/routes";
import {buildCreateRequestPayload} from "../../helpers/grantApplicationDataMap/buildCreateRequestPayload";
import useUserData from "../user/useUserData";
import {enqueueSnackbar} from "notistack";
import {getEndPoint} from "../../helpers/constants";
import axios from "axios";
import {useTranslation} from "react-i18next";
import {PAGE_APPLICATIONS} from "../../locales/pages/namespaces";
import {
    generateApplicationPdfBase64
} from "../../components/custom/generateApplicationPdfBase64/generateApplicationPdfBase64";


const mapOrganizationToFormValues = (org, currentValues) => ({
    ...currentValues,
    country: org.CountryId || "",
    orgName: org.AccountNameEL || org.AccountName || "",
    orgName_en: org.AccountName || "",
    address1: org.StreetEL || org.Street || "",
    address2: org.StrNumber || "",
    city: org.CityEL || org.City || "",
    state: org.County || "",
    zip: org.PostalCode || "",
    phone: org.Telephone1 || "",
    email: org.Emailaddress || "",
    website: org.Websiteurl || "",
    facebook: org.Facebook || "",
    x: org.Twitter || "",
    instagram: org.Instagram || "",
    otherSocial: org.Othersocialmedia || "",
});

export default function useGrantApplicationForm(
                                                totalSteps = 5,
                                                campaign,
                                                ) {
    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState({});
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const language = useSelector(state => state.currentUser.get("language"));
    const userData = useUserData();
    const {t} = useTranslation(PAGE_APPLICATIONS)
    const [organizationLoading, setOrganizationLoading] = useState(false);
    const [pendingOrganizationStep, setPendingOrganizationStep] = useState(false);

    console.log('language',language)

    const stepSchemas = [
        // STEP 1: TIN
        Yup.object({
            tin: Yup.string()
                .required(t("TIN is required"))
                // .matches(/^[0-9]{9}$/, t("TIN must be 9 digits")),
        }),

        // STEP 2: Organization details
        Yup.object({
            country: Yup.string().required(t("Country is required")),
            orgName: Yup.string().when([], {
                is: () => language === "el",
                then: (schema) =>
                    schema
                        .required(t("Organization name is required"))
                        .matches(
                            /^[0-9Α-Ωα-ωΆ-Ώά-ώ\s]+$/,
                            t("Only Greek characters are allowed")
                        ),
                otherwise: (schema) => schema.notRequired(),
            }),

            orgName_en: Yup.string()
                .matches(
                    /^[0-9A-Za-z\s]+$/,
                    t("Only Latin characters are allowed")
                )
                .when([], {
                    is: () => language === "en",
                    then: (schema) =>
                        schema.required(t("Organization name is required")),
                    otherwise: (schema) => schema.required(t("Organization name is required")),
                }),


            address1: Yup.string().required(t("Address Line 1 is required")),
            address2: Yup.string(),

            city: Yup.string().required(t("City is required")),
            state: Yup.string().required(t("State/Prefecture is required")),
            zip: Yup.string().required(t("Zip is required")),

            phone: Yup.string()
                .required(t("Phone line is required"))
                .matches(
                    /^\+[0-9]{8,15}$/,
                    t("Be sure to include your country code and only numbers")
                ),
            email: Yup.string().email(t("Invalid email")).required(t("Email is required")),
            learnForUs:Yup.string().max(100, t("Max 100 characters")),
            // orgMission: Yup.string()
            //     .required(t("Organization mission is required"))
            //     .max(3000, t("Max 3000 characters")),
            //
            // orgRegisteredAsPer: Yup.string()
            //     .required(t("Registered as per is required")),
            //
            // orgDateEstablished: Yup.date()
            //     .required(t("Date Established is required")),
            //
            // orgFiscalSponsor: Yup.string(),
            website: Yup.string(),
            facebook: Yup.string(),
            x: Yup.string(),
            instagram: Yup.string(),
            otherSocial: Yup.string(),

            authorized: Yup.boolean().oneOf(
                [true],
                t("You must confirm that you are authorized")
            ),
        }),

        // STEP 3: Representative
        Yup.object({
            // legal representative
            repTitle: Yup.string().required(t("Title is required")),
            repFirstName: Yup.string().when([], {
                is: () => language === "el",
                then: (schema) =>
                    schema
                        .required(t("First name is required"))
                        .matches(
                            /^[0-9Α-Ωα-ωΆ-Ώά-ώ\s]+$/,
                            t("Only Greek characters are allowed")
                        ),
                otherwise: (schema) => schema.notRequired(),
            }),

            repFirstName_en: Yup.string()
                .matches(
                    /^[0-9A-Za-z\s]+$/,
                    t("Only Latin characters are allowed")
                )
                .when([], {
                    is: () => language === "en",
                    then: (schema) =>
                        schema.required(t("First name is required")),
                    otherwise: (schema) => schema.required(t("First name is required")),
                }),
            repLastName: Yup.string().when([], {
                is: () => language === "el",
                then: (schema) =>
                    schema
                        .required(t("Last name is required"))
                        .matches(
                            /^[0-9Α-Ωα-ωΆ-Ώά-ώ\s]+$/,
                            t("Only Greek characters are allowed")
                        ),
                otherwise: (schema) => schema.notRequired(),
            }),

            repLastName_en: Yup.string()
                .matches(
                    /^[0-9A-Za-z\s]+$/,
                    t("Only Latin characters are allowed")
                )
                .when([], {
                    is: () => language === "en",
                    then: (schema) =>
                        schema.required(t("Last name is required")),
                    otherwise: (schema) => schema.required(t("Last name is required")),
                }),
            repPositionTitle: Yup.string().required(t("Position/Title is required")),
            //repState: Yup.string().required(t("State/Prefecture is required")),
            repZip: Yup.string().required(t("Zip is required")),
            repFixedPhone: Yup.string(),
            repMobilePhone: Yup.string()
                .required(t("Mobile phone line is required"))
                .matches(
                    /^\+[0-9]{8,15}$/,
                    t("Be sure to include your country code and only numbers")
                ),

            repEmail: Yup.string().email(t("Invalid email")).required(t("Email is required")),

            repIsContact: Yup.boolean(),

            // contact person (only required if repIsContact === false)
            contactTitle: Yup.string().when("repIsContact", {
                is: false,
                then: (s) => s.required(t("Title is required")),
                otherwise: (s) => s,
            }),
            contactFirstName: Yup.string().when("repIsContact", {
                is: false,
                then: (schema) =>
                    language === "el"
                        ? schema
                            .required(t("First name is required"))
                            .matches(
                                /^[0-9Α-Ωα-ωΆ-Ώά-ώ\s]+$/,
                                t("Only Greek characters are allowed")
                            )
                        : schema.notRequired(),
                otherwise: (schema) => schema.notRequired(),
            }),

            contactFirstName_en: Yup.string().when("repIsContact", {
                is: false,
                then: (schema) =>
                    language === "en"
                        ? schema
                            .required(t("First name is required"))
                            .matches(
                                /^[0-9A-Za-z\s]+$/,
                                t("Only Latin characters are allowed")
                            )
                        : schema
                            .matches(
                                /^[0-9A-Za-z\s]+$/,
                                t("Only Latin characters are allowed")
                            )
                            .required(t("First name is required")),
                otherwise: (schema) => schema.notRequired(),
            }),

            contactLastName: Yup.string().when("repIsContact", {
                is: false,
                then: (schema) =>
                    language === "el"
                        ? schema
                            .required(t("Last name is required"))
                            .matches(
                                /^[0-9Α-Ωα-ωΆ-Ώά-ώ\s]+$/,
                                t("Only Greek characters are allowed")
                            )
                        : schema.notRequired(),
                otherwise: (schema) => schema.notRequired(),
            }),

            contactLastName_en: Yup.string().when("repIsContact", {
                is: false,
                then: (schema) =>
                    language === "en"
                        ? schema
                            .required(t("Last name is required"))
                            .matches(
                                /^[0-9A-Za-z\s]+$/,
                                t("Only Latin characters are allowed")
                            )
                        : schema
                            .matches(
                                /^[0-9A-Za-z\s]+$/,
                                t("Only Latin characters are allowed")
                            )
                            .required(t("Last name is required")), // αν ΔΕΝ το θες required σε el, άλλαξέ το σε notRequired()
                otherwise: (schema) => schema.notRequired(),
            }),

            contactPositionTitle: Yup.string().when("repIsContact", {
                is: false,
                then: (s) => s.required(t("Position/Title is required")),
            }),
            // contactState: Yup.string().when("repIsContact", {
            //     is: false,
            //     then: (s) => s.required(t("State/Prefecture is required")),
            // }),
            contactZip: Yup.string().when("repIsContact", {
                is: false,
                then: (s) => s.required(t("Zip is required")),
            }),
            contactFixedPhone: Yup.string(),
            contactMobilePhone: Yup.string().when("repIsContact", {
                is: false,
                then: (s) =>
                    s
                        .required(t("Mobile phone line is required"))
                        .matches(
                            /^\+[0-9]{8,15}$/,
                            t("Be sure to include your country code and only numbers")
                        ),
            }),
            contactEmail: Yup.string()
                .email(t("Invalid email"))
                .when("repIsContact", {
                    is: false,
                    then: (s) => s.required(t("Email is required")),
                }),
        }),

        // STEP 4: Request Details
        Yup.object({
            historyMission: Yup.string()
                .required(t("History and mission is required"))
                .max(3000, t("Max 3000 characters")),
            requestDescription: Yup.string()
                .required(t("Request description is required"))
                .max(3000, t("Max 3000 characters")),
            amount: Yup.number()
                .typeError(t("Amount must be a number"))
                .integer(t("Amount must be an integer"))
                .min(1, t("Amount must be greater than 0"))
                .required(t("Amount is required")),
            currency: Yup.string().required(t("Currency is required")),
            // campaignAgreement: Yup.boolean()
            //     .oneOf([true], t("You must confirm that funding will be expended by June 2027")),
        }),

        // STEP 5: Done (no validation)
        Yup.object({}),
    ];



    const initialValues = {
        // step1
        tin: "",

        // step2
        country: "",
        orgName: "",
        orgName_en: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
        phone: "",
        email: "",
        website: "",
        facebook: "",
        x: "",
        instagram: "",
        otherSocial: "",
        authorized: false,
        learnForUs:"",

        // step3:
        repTitle: "",
        repFirstName: "",
        repFirstName_en: "",
        repLastName: "",
        repLastName_en: "",
        repPositionTitle: "",
        repState: "",
        repZip: "",
        repFixedPhone: "",
        repMobilePhone: "",
        repEmail: "",
        repIsContact: false,

        // step3: contact
        contactTitle: "",
        contactFirstName: "",
        contactFirstName_en: "",
        contactLastName: "",
        contactLastName_en: "",
        contactPositionTitle: "",
        contactState: "",
        contactZip: "",
        contactFixedPhone: "",
        contactMobilePhone: "",
        contactEmail: "",

        // step4
        historyMission: "",
        requestDescription: "",
        amount: "",
        currency: "",

        orgMission: "",
        orgRegisteredAsPer: "",
        orgDateEstablished: "",
        orgFiscalSponsor: "",

        //campaignAgreement: false,
    };

    const history = useHistory()
    const {
        fetchCountries,
        fetchCounties,
        fetchCurrencies,
        findOrganization,
        createRequest,
        requestNumber
    } = useGrantApplicationAction();

    const {
        countries = [],
        counties = [],
        currencies = [],
        organization = null,
        loading = false,
        error = null,
    } = useSelector((state) => state.grant || {});


    useEffect(() => {
        if (!pendingOrganizationStep) return;
        if (loading) return;

        if (organization?.ORGANIZATION) {
            const org = organization.ORGANIZATION;

            const nextValues = mapOrganizationToFormValues(org, formik.values);

            formik.resetForm({
                values: nextValues,
            });

            setCompleted((prev) => ({ ...prev, [0]: true }));
            setPendingOrganizationStep(false);

            setTimeout(() => {
                setActiveStep(1);
            }, 0);

            return;
        }else {
            setPendingOrganizationStep(false);
            setActiveStep(1);
        }

        if (error) {
            setPendingOrganizationStep(false);
            setActiveStep(1);
            return;
        }
    }, [pendingOrganizationStep, loading, organization, error]);

//     useEffect(() => {
//         if (!organization) return;
// console.log('organization',organization.ORGANIZATION)
//         formik.setFieldValue("country", organization.ORGANIZATION.CountryId || "");
//         formik.setFieldValue("orgName", organization.ORGANIZATION.AccountNameEL || organization.ORGANIZATION.AccountName || "");
//         formik.setFieldValue("orgName_en", organization.ORGANIZATION.AccountName || "");
//         formik.setFieldValue("address1", organization.ORGANIZATION.StreetEL || organization.ORGANIZATION.Street || "");
//         formik.setFieldValue("address2", organization.ORGANIZATION.StrNumber || "");
//         formik.setFieldValue("city", organization.ORGANIZATION.CityEL || organization.ORGANIZATION.City || "");
//         formik.setFieldValue("state", organization.ORGANIZATION.County || "");
//         formik.setFieldValue("zip", organization.ORGANIZATION.PostalCode || "");
//         formik.setFieldValue("phone", organization.ORGANIZATION.Telephone1 || "");
//         formik.setFieldValue("email", organization.ORGANIZATION.Emailaddress || "");
//         formik.setFieldValue("website", organization.ORGANIZATION.Websiteurl || "");
//         formik.setFieldValue("facebook", organization.ORGANIZATION.Facebook || "");
//         formik.setFieldValue("x", organization.ORGANIZATION.Twitter || "");
//         formik.setFieldValue("instagram", organization.ORGANIZATION.Instagram || "");
//         formik.setFieldValue("otherSocial", organization.ORGANIZATION.Othersocialmedia || "");
//     }, [organization]);


    const handleCountyChange = (id) => {
        formik.setFieldValue("state", id);
        fetchCounties({ id });
    };

    const fetchRequestNumber = async () => {
        const url = getEndPoint("requestNumber");

        const res = await axios.post(url, {
            action: "fetch new request number"
        });

        return res.data.newGrantApplicationNumber;
    };

    const handleSubmitRequest = async () => {
        try {
            const reqNumResponse = await fetchRequestNumber();
            if (!reqNumResponse) {
                throw new Error("Failed to fetch request number");
            }
            const finalReqNumber = `GRA-${reqNumResponse}`;
            const copyPdfBase64 = await generateApplicationPdfBase64({
                values: formik.values,
                user: userData,
                applicationId: finalReqNumber,
                language,
                pdfType: campaign === "30years" ? "30years" : "apply",
                countries,
                counties,
                currencies

            });
            const payload = buildCreateRequestPayload(formik.values, userData, finalReqNumber,language,copyPdfBase64,campaign);
            const response = await createRequest(payload);
            setShowSuccessModal(true);
            setActiveStep(4);

        } catch (err) {
            enqueueSnackbar(t("Submission failed"), { variant: "error" });
        }
    };

    useEffect(() => {
        if (activeStep === 0) {
            fetchCountries({});
            fetchCounties({});
            fetchCurrencies({});
        }
    }, [activeStep]);


    const formik = useFormik({
        initialValues,
        validationSchema: stepSchemas[activeStep],
        validateOnBlur: true,
        validateOnChange: false,
        enableReinitialize: false,
        onSubmit: () => {},
    });

    const handleNext = async () => {
        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {
            formik.setTouched(
                Object.keys(errors).reduce((acc, key) => {
                    acc[key] = true;
                    return acc;
                }, {})
            );
            return;
        }

        if (activeStep === 0) {
            formik.setValues({
                ...formik.values,
                tin: formik.values.tin,

                country: '',
                orgName: '',
                orgName_en: '',
                address1: '',
                address2: '',
                city: '',
                state: '',
                zip: '',
                phone: '',
                email: '',
                website: '',
                facebook: '',
                x: '',
                instagram: '',
                otherSocial: '',
                authorized: false,
                learnForUs: '',

            });

            setPendingOrganizationStep(true);
            findOrganization({ trn: formik.values.tin });
            return;
        }

        if (activeStep === 3) {
            handleSubmitRequest();
            return;
        }

        setCompleted((prev) => ({ ...prev, [activeStep]: true }));
        if (activeStep < totalSteps - 1) {
            setActiveStep((prev) => prev + 1);
        }
    };


    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep((prev) => prev - 1);
        }else if (activeStep === 0){
            history.push(getRouteUrl(ROUTE_PAGE_HOME,{campaign}))
        }
    };

    const goToStep = (stepIndex) => {
        setActiveStep(stepIndex);
    };


    return {
        formik,
        activeStep,
        completed,
        handleNext,
        handleBack,
        goToStep,
        totalSteps,
        handleSubmitRequest,
        countries,
        counties,
        currencies,
        organization,
        loading,
        error,
        language,
        handleCountyChange,
        organizationLoading,
        requestNumber,
        showSuccessModal,
        setShowSuccessModal,
    };
}
